/**
 * ProcurementBadgeSubscriber — pushes "pending approvals" (approver
 * role) and "my requests in flight" (everyone) via SignalR.
 *
 * Phase 4 of the badge migration. Deltas come from PurchaseRequest
 * Submitted/Approved/Rejected consumers; the approver-side counter is
 * Clients.All, the submitter-side is Clients.User.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useProcurementDashboardService,
  usePurchaseRequestService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useProcurementBadgesStore } from './procurementBadgesStore';

const APPROVER_ROLES = ['Admin', 'Procurement Admin'];
const IN_FLIGHT_STATUS_IDS = new Set([1, 2, 3, 4, 5]);
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[ProcurementBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface ProcurementDeltas {
  pendingApprovals?: number;
  myRequestsInFlight?: number;
}

export function ProcurementBadgeSubscriber() {
  const { hasAnyRole, user } = useEnrichedAuth();
  const isApprover = hasAnyRole(APPROVER_ROLES);
  const isAuthed = !!user;

  const dashboardService = useProcurementDashboardService();
  const purchaseRequestService = usePurchaseRequestService();
  const setFromMetrics = useProcurementBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useProcurementBadgesStore((s) => s.applyDelta);
  const reset = useProcurementBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const [stats, mine] = await Promise.all([
        isApprover ? (dashboardService.getMyStats() as Promise<any>) : Promise.resolve(null),
        isAuthed ? (purchaseRequestService.getMyListView() as Promise<any[]>) : Promise.resolve([]),
      ]);
      const pendingApprovals = isApprover && stats
        ? (stats.pendingLineApprovals ?? 0) + (stats.pendingFinanceApprovals ?? 0)
        : 0;
      const myRequestsInFlight = (mine ?? []).filter((r: any) =>
        IN_FLIGHT_STATUS_IDS.has(r.statusID),
      ).length;
      log('baseline', { pendingApprovals, myRequestsInFlight });
      setFromMetrics({ pendingApprovals, myRequestsInFlight });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [isApprover, isAuthed, dashboardService, purchaseRequestService, setFromMetrics]);

  useEffect(() => {
    if (!isAuthed) return;
    refetchBaseline();
  }, [isAuthed, refetchBaseline]);

  // Procurement hub is gated on Admin/Procurement/Manager/Procurement Admin —
  // non-admin authed users still need the connection for myRequestsInFlight,
  // but the hub will reject them. Skip the WS hookup unless the user is an
  // approver; non-admin users rely on the REST baseline only.
  const wsEnabled = isApprover;
  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('procurement', {
    autoConnect: wsEnabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<ProcurementDeltas>) => {
      if (msg.domain !== 'Procurement') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<ProcurementDeltas>) => {
      if (msg.domain !== 'procurement') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!wsEnabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [wsEnabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { isApprover, isAuthed });
  }, [isApprover, isAuthed]);

  return null;
}
